import {Component, Injector, Input, OnInit, ViewChild} from '@angular/core';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {
  ClientConfigurationScheme,
  ComponentConfiguration,
  ComponentInit,
  fromPageEvent,
  InitialConfig
} from '@twpub/core/utils';
import {FieldProps, ResultList} from '@twpub/core/models';
import {DEFAULT_PAGE_SIZE, ResultListProp} from '@twpub/core/constants';
import {ComponentCategory, ControlType} from '@twpub/core/enums';
import {Validators} from '@angular/forms';
import {BaseResultListComponent} from '@twpub/shared/components/base';
import {NgClass} from '@angular/common';
import {AngularSplitModule} from 'angular-split';
import {MatCell, MatCellDef, MatColumnDef, MatRow, MatRowDef, MatTable} from '@angular/material/table';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {TermRenderComponent} from '@twpub/shared/components/renders';
import {ResultViewWrapperComponent} from '@twpub/shared/components/wrappers';

@Component({
  selector: 'pub-search-result-list',
  templateUrl: './default-result-list.component.html',
  styleUrls: ['./default-result-list.component.scss'],
  standalone: true,
  imports: [NgClass, AngularSplitModule, MatTable, MatColumnDef, MatCellDef, MatCell, TermRenderComponent, MatRowDef, MatRow, MatPaginator, MatProgressSpinner, ResultViewWrapperComponent]
})
export class DefaultResultListComponent extends BaseResultListComponent implements ResultList, OnInit {
  private static readonly defaultPageSizeOptions = [10, 25, 50];

  @Input() selectedLang?: string;
  pageSizeOptions = DefaultResultListComponent.defaultPageSizeOptions;
  loadingResultsLabel = $localize`:@@defaultResultList.loadingResults:Loading results...`;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  static readonly compName = 'DefaultResultListComponent';

  static Init = class extends ComponentInit {
    readonly scheme = ClientConfigurationScheme.DEFAULT;
    override props: FieldProps[] = [
      {
        key: ResultListProp.InitialSearch,
        label: 'Results visible on page load',
        controlType: ControlType.Boolean,
        description: 'Automatically list all terms when page is opened, before any search'
      },
      {
        key: ResultListProp.PageSize,
        label: 'Page size',
        controlType: ControlType.Number,
        description: 'How many results to show per page initially. Defaults to 25.'
      },
      {
        key: ResultListProp.PageSizeOptions,
        label: 'Selectable page sizes',
        controlType: ControlType.Textbox,
        description: 'Comma-separated page sizes shown in the paginator. Defaults to 10,25,50.',
        validators: [Validators.pattern(/^\s*\d+\s*(,\s*\d+\s*)*$/)],
        validationMessage: 'Must be numbers separated by commas (e.g. 10,25,50)'
      }
    ];

    constructor() {
      // eslint-disable-next-line no-undef
      super(DefaultResultListComponent, DefaultResultListComponent.compName, 'Default Result List', [ComponentCategory.ResultList]);
    }

    createConfig(initialConfig: InitialConfig = undefined) {
      return new ComponentConfiguration<ResultList>(DefaultResultListComponent, this.name, initialConfig);
    }
  }

  constructor(private injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    this.onInit();
    this.initializePageSizeOptions();
    this.fetchLangNames();
    this.performSearch(this.sharedStateService.getPaginationData(), true);
  }

  pageChange(event: PageEvent) {
    this.performSearch(fromPageEvent(event), true);
  }

  private initializePageSizeOptions(): void {
    const configuredPageSize = this.config.getNumber(ResultListProp.PageSize, DEFAULT_PAGE_SIZE) ?? DEFAULT_PAGE_SIZE;
    const currentPaginator = this.sharedStateService.getPaginationData();

    // Apply the configured initial page size whenever the result list loads.
    // resultPaginator (incl. totalItems) is persisted in sessionStorage, so a
    // totalItems-based "fresh load" guard would skip this on every load after
    // the first search and silently ignore the admin-configured page size.
    if (currentPaginator && currentPaginator.pageSize !== configuredPageSize) {
      this.sharedStateService.setPaginationData({...currentPaginator, pageSize: configuredPageSize});
    }

    const pageSizeOptions = this.config.getString(ResultListProp.PageSizeOptions)
      ?.split(',')
      .map(value => Number.parseInt(value.trim(), 10))
      .filter(value => Number.isFinite(value) && value > 0);

    const uniqueOptions = pageSizeOptions?.length
      ? Array.from(new Set(pageSizeOptions))
      : DefaultResultListComponent.defaultPageSizeOptions;

    this.pageSizeOptions = uniqueOptions.includes(configuredPageSize)
      ? uniqueOptions
      : [...uniqueOptions, configuredPageSize].sort((a, b) => a - b);
  }

  protected override shouldOpenSingleResult = (): boolean => true;
}
